import React from 'react';
import { Image } from 'views/components/atoms/Image';
import styled from '@emotion/styled';
import { Typography } from 'views/components/atoms/Typography';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import { Button } from 'views/components/atoms/Button';
import { Color, FontSize, MAX_WIDTH } from 'views/constants';
import { LinkText } from 'views/components/molecules/LinkText';
import { localeIds } from 'locals';

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const IconWrapper = styled.div`
  > div {
    width: 54px;
    height: 54px;
  }
`;

const ContentsWrapper = styled.div`
  width: 80%;

  > :not(:last-child) {
    margin-bottom: 4px;
  }
`;

const InstallContentsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const InstallButtonWrapper = styled.div`
  width: 110px;
  margin-left: 10px;
`;

type Device = {
  target: number;
  current: number;
};

type OsVersion = {
  minimum: string;
  device: string;
};

type Notice = {
  binaryOverSize?: boolean;
  duplicateIcon?: boolean;
  enterpriseAcceptProfile?: boolean;
  adhocEnableDeveloperMode?: boolean;
};

interface AppInstallProps {
  iconSrc: string;
  title: string;
  revision: number;
  ownerName: string;
  bundleIdentifier: string;
  version: string;
  updatedDate: Date;
  writableRole: boolean;
  device: Device;
  osVersion: OsVersion;
  teamName: string;
  notice: Notice;
  onInstall(): void;
  onOverwriteInstall(): void;
  onUnsupportedOverwriteInstall(): void;
  unsupportedOverwriteInstall: boolean;
  hideOverwriteInstall: boolean;
  notRegisteredIphone: boolean;
  expiredProvisioningProfile: boolean;
  unsupportedDevice: boolean;
  unsupportedOsVersion: boolean;
  distribution?: {
    title: string;
    updatedDate: Date;
  };
  installReady: boolean;
}

export const AppInstall: React.FC<AppInstallProps> = ({
  iconSrc,
  title,
  revision,
  ownerName,
  bundleIdentifier,
  version,
  updatedDate,
  writableRole,
  device,
  osVersion,
  teamName,
  notice,
  onInstall,
  onOverwriteInstall,
  onUnsupportedOverwriteInstall,
  unsupportedOverwriteInstall,
  hideOverwriteInstall,
  notRegisteredIphone,
  expiredProvisioningProfile,
  unsupportedDevice,
  unsupportedOsVersion,
  distribution,
  installReady,
}) => {
  let installDisabledNotice: React.ReactNode | null = null;
  const installDisabled =
    unsupportedDevice ||
    unsupportedOsVersion ||
    notRegisteredIphone ||
    expiredProvisioningProfile;

  if (unsupportedDevice) {
    installDisabledNotice = (
      <FormattedMessage id={'deviceFamilyNames.' + device.target}>
        {(targetDeviceName: string): React.ReactElement => (
          <FormattedMessage id={'deviceFamilyNames.' + device.current}>
            {(currentDeviceName: string): React.ReactElement => (
              <FormattedMessage
                id={localeIds.appInstall.unsupportedDevice}
                values={{
                  targetDevice: targetDeviceName,
                  currentDevice: currentDeviceName,
                }}
              />
            )}
          </FormattedMessage>
        )}
      </FormattedMessage>
    );
  }
  if (unsupportedOsVersion) {
    installDisabledNotice = (
      <FormattedMessage
        id={localeIds.appInstall.unsupportedOsVersion}
        values={{
          minimumOsVersion: osVersion.minimum,
          deviceOsVersion: osVersion.device,
        }}
      />
    );
  }
  if (notRegisteredIphone) {
    if (writableRole) {
      installDisabledNotice = (
        <FormattedMessage
          id={localeIds.appInstall.notRegisteredIphone.developer}
        />
      );
    } else {
      installDisabledNotice = (
        <FormattedMessage
          id={localeIds.appInstall.notRegisteredIphone.tester}
        />
      );
    }
  }
  if (expiredProvisioningProfile) {
    if (writableRole) {
      installDisabledNotice = (
        <FormattedMessage
          id={localeIds.appInstall.expiredProvisioningProfile.developer}
        />
      );
    } else {
      installDisabledNotice = (
        <FormattedMessage
          id={localeIds.appInstall.expiredProvisioningProfile.tester}
        />
      );
    }
  }

  return (
    <Wrapper>
      <IconWrapper>
        <div>
          <Image src={iconSrc} />
        </div>
      </IconWrapper>
      <ContentsWrapper>
        <Typography bold ellipsis block>
          {title}
        </Typography>
        <Typography size={FontSize.S} color={Color.C3} block>
          #{revision}
        </Typography>
        <Typography size={FontSize.S} color={Color.C3} block preWrap>
          {bundleIdentifier}
        </Typography>
        <Typography size={FontSize.S} color={Color.C3} block ellipsis>
          {distribution
            ? `via ${distribution.title} / by ${ownerName}`
            : `by ${ownerName}`}
        </Typography>
        <Typography size={FontSize.S} color={Color.C3} block>
          <FormattedMessage id={localeIds.appInstall.version} />: {version}
        </Typography>
        <Typography size={FontSize.S} color={Color.C3} block>
          <FormattedMessage id={localeIds.appInstall.updatedAt} />:{' '}
          {<FormattedDate value={updatedDate} format="default" />}{' '}
          {<FormattedTime value={updatedDate} format="default" />}
        </Typography>

        {!installDisabled && notice.binaryOverSize ? (
          <Typography size={FontSize.XS} color={Color.C3} block>
            <FormattedMessage id={localeIds.appInstall.binaryOverSize} />
          </Typography>
        ) : null}

        {installDisabledNotice ? (
          <Typography size={FontSize.XS} color={Color.C3} block>
            {installDisabledNotice}
          </Typography>
        ) : null}

        <InstallContentsWrapper>
          {!installReady ||
          installDisabled ||
          hideOverwriteInstall ||
          unsupportedOverwriteInstall ? null : (
            <LinkText to="#" onClick={onOverwriteInstall}>
              <Typography size={FontSize.XS} color={Color.C3}>
                <FormattedMessage id={localeIds.appInstall.overwriteInstall} />
              </Typography>
            </LinkText>
          )}
          {unsupportedOverwriteInstall ? (
            <LinkText to="#" onClick={onUnsupportedOverwriteInstall}>
              <Typography size={FontSize.XS} color={Color.C3}>
                <FormattedMessage id={localeIds.appInstall.overwriteInstall} />
              </Typography>
            </LinkText>
          ) : null}
          <InstallButtonWrapper>
            <Button
              type="button"
              onClick={onInstall}
              style="primary"
              fontSize={FontSize.XS}
              height="32px"
              disabled={installDisabled || !installReady}
            >
              <FormattedMessage id={localeIds.appInstall.install} />
            </Button>
          </InstallButtonWrapper>
        </InstallContentsWrapper>

        {notice.duplicateIcon ? (
          <Typography size={FontSize.S} color={Color.C3} block>
            <FormattedMessage id={localeIds.appInstall.duplicateIconNotice} />
          </Typography>
        ) : null}

        {notice.enterpriseAcceptProfile ? (
          <Typography size={FontSize.S} color={Color.C3} block>
            <FormattedMessage
              id={localeIds.appInstall.enterpriseAcceptProfile}
              values={{ teamName }}
            />
          </Typography>
        ) : null}

        {notice.adhocEnableDeveloperMode ? (
          <Typography size={FontSize.S} color={Color.C3} block>
            <FormattedMessage
              id={localeIds.appInstall.adhocEnableDeveloperMode}
            />
          </Typography>
        ) : null}
      </ContentsWrapper>
    </Wrapper>
  );
};
